import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getApiOrigin } from '../config/environment';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';
import type { LocationProvider } from '../types/provider';

const PETRONA = '"Petrona", Georgia, "Times New Roman", serif';
const SHERLOOK_LOGO_SRC = `${process.env.PUBLIC_URL}/assets/sherlook-logo.svg`;
const SPLASH_CARD_RADIUS = 12;
const SPLASH_CARD_BUTTON_RADIUS = 10;

function partnerCardWrapSx() {
  return {
    display: 'flex',
    flex: {
      xs: '1 1 100%',
      sm: '1 1 calc(33.333% - 8px)',
    },
    minWidth: {
      xs: '100%',
      sm: 0,
    },
    maxWidth: {
      xs: '100%',
      sm: 'calc(33.333% - 8px)',
    },
    boxSizing: 'border-box' as const,
    alignSelf: 'stretch',
  };
}

export type SplashPreviewPartner = {
  provider_id: number;
  title: string;
  date_text?: string;
  location_text?: string;
  year_badge: number;
  cover_image?: string;
  logo_url?: string | null;
  button_color: string;
  badge_color: string;
};

export type SplashPreviewTextColors = {
  season_intro: string;
  heading: string;
  subheading: string;
  events_section_title: string;
  continue_button: string;
  continue_button_border: string;
  footer_text: string;
  card_title: string;
  card_meta: string;
};

type Props = {
  seasonIntro: string;
  heading: string;
  subheading: string;
  eventsSectionTitle: string;
  continueButtonLabel: string;
  searchButtonLabel: string;
  footerText: string;
  icons: Record<string, string>;
  textColors: SplashPreviewTextColors;
  partners: SplashPreviewPartner[];
};

function resolveAssetUrl(path?: string | null): string | null {
  if (!path?.trim()) return null;
  const trimmed = path.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `${getApiOrigin()}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

function PreviewIcon({
  src,
  fallback,
  size = 20,
}: {
  src?: string | null;
  fallback: React.ReactNode;
  size?: number;
}) {
  const resolved = resolveAssetUrl(src);
  if (resolved) {
    return (
      <Box
        component="img"
        src={resolved}
        alt=""
        sx={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      />
    );
  }
  return <>{fallback}</>;
}

function PreviewPartnerCard({
  partner,
  searchLabel,
  calendarIcon,
  locationIcon,
  cardTitleColor,
  cardMetaColor,
}: {
  partner: SplashPreviewPartner;
  searchLabel: string;
  calendarIcon?: string | null;
  locationIcon?: string | null;
  cardTitleColor: string;
  cardMetaColor: string;
}) {
  const cover = resolveAssetUrl(partner.cover_image);
  const logo = resolveProviderLogoUrl(partner.logo_url);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${SPLASH_CARD_RADIUS}px`,
        overflow: 'hidden',
        border: '1px solid #E2E8F0',
        bgcolor: '#fff',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box sx={{ position: 'relative', height: 132, flexShrink: 0, bgcolor: partner.button_color }}>
        {cover ? (
          <Box component="img" src={cover} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            pointerEvents: 'none',
          }}
        >
          {logo ? (
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{
                maxWidth: '92%',
                maxHeight: 88,
                objectFit: 'contain',
                filter: cover ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))' : 'none',
              }}
            />
          ) : (
            <Typography sx={{ color: '#fff', fontWeight: 700, px: 1, textAlign: 'center', fontSize: 13 }}>
              {partner.title}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            px: 0.9,
            py: 0.25,
            borderRadius: '8px',
            bgcolor: partner.badge_color,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {partner.year_badge}
        </Box>
      </Box>
      <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 118 }}>
        <Typography
          sx={{
            fontFamily: PETRONA,
            fontWeight: 900,
            fontSize: 11,
            color: cardTitleColor,
            mb: 0.75,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={partner.title}
        >
          {partner.title}
        </Typography>
        <Box sx={{ minHeight: 18, mb: partner.date_text ? 0.5 : 0 }}>
        {partner.date_text ? (
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <PreviewIcon
              src={calendarIcon}
              fallback={<CalendarMonthOutlinedIcon sx={{ fontSize: 14, color: cardMetaColor }} />}
              size={14}
            />
            <Typography
              sx={{
                fontSize: 10,
                color: cardMetaColor,
                lineHeight: 1.35,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={partner.date_text}
            >
              {partner.date_text}
            </Typography>
          </Stack>
        ) : null}
        </Box>
        <Box sx={{ minHeight: 18, mb: 1, flex: 1 }}>
        {partner.location_text ? (
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <PreviewIcon
              src={locationIcon}
              fallback={<PlaceOutlinedIcon sx={{ fontSize: 14, color: cardMetaColor }} />}
              size={14}
            />
            <Typography
              sx={{
                fontSize: 10,
                color: cardMetaColor,
                lineHeight: 1.35,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={partner.location_text}
            >
              {partner.location_text}
            </Typography>
          </Stack>
        ) : null}
        </Box>
        <Button
          fullWidth
          disabled
          sx={{
            mt: 'auto',
            bgcolor: partner.button_color,
            color: '#fff',
            borderRadius: `${SPLASH_CARD_BUTTON_RADIUS}px`,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 12,
            py: 0.75,
          }}
        >
          {searchLabel}
        </Button>
      </Box>
    </Box>
  );
}

export function buildSplashPreviewPartners(
  selectedProviderIds: number[],
  providerEntries: Record<number, {
    cover_image: string;
    button_color: string;
    badge_color: string;
    title: string;
    date_text: string;
    location_text: string;
  }>,
  providersById: Map<number, LocationProvider>
): SplashPreviewPartner[] {
  const partners: SplashPreviewPartner[] = [];

  for (const providerId of selectedProviderIds) {
    const provider = providersById.get(providerId);
    if (!provider) continue;

    const entry = providerEntries[providerId];
    const buttonColor = entry?.button_color || provider.private_page_background_color || '#1E4B8F';

    partners.push({
      provider_id: providerId,
      title: entry?.title?.trim() || provider.name.toUpperCase(),
      date_text: entry?.date_text?.trim() || 'Dates à venir',
      location_text: entry?.location_text?.trim() || provider.address?.city?.toUpperCase() || 'Lieu à confirmer',
      year_badge: new Date().getFullYear(),
      cover_image: entry?.cover_image || undefined,
      logo_url: provider.logo_url ?? provider.logo ?? null,
      button_color: buttonColor,
      badge_color: entry?.badge_color || buttonColor,
    });
  }

  return partners;
}

const HomeSplashScreenPreview: React.FC<Props> = ({
  seasonIntro,
  heading,
  subheading,
  eventsSectionTitle,
  continueButtonLabel,
  searchButtonLabel,
  footerText,
  icons,
  textColors,
  partners,
}) => {
  return (
    <Box
      sx={{
        borderRadius: '24px',
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid #E2E8F0',
        boxShadow: '0 16px 40px rgba(15, 23, 42, 0.12)',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box component="img" src={SHERLOOK_LOGO_SRC} alt="Sherlook" sx={{ height: 28 }} />
        <IconButton size="small" disabled>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: partners.length > 0 ? 'minmax(0, 0.72fr) minmax(0, 1.55fr)' : '1fr',
          gap: 2,
          px: 2.5,
          pb: 2,
        }}
      >
        <Stack spacing={1.5} justifyContent="center" sx={{ maxWidth: 320 }}>
          <Typography sx={{ fontFamily: PETRONA, fontStyle: 'italic', fontWeight: 700, fontSize: '1.2rem', color: textColors.season_intro }}>
            {seasonIntro}
          </Typography>
          <Typography sx={{ fontFamily: PETRONA, fontWeight: 900, fontSize: '1.75rem', color: textColors.heading, lineHeight: 1.1 }}>
            {heading}
          </Typography>
          <Typography sx={{ color: textColors.subheading, fontSize: '0.95rem', lineHeight: 1.4 }}>
            {subheading}
          </Typography>
          <Button
            disabled
            endIcon={
              <PreviewIcon
                src={icons.continue_arrow}
                fallback={<ArrowForwardIcon sx={{ color: textColors.continue_button }} />}
                size={16}
              />
            }
            sx={{
              alignSelf: 'flex-start',
              px: 2.5,
              py: 1,
              borderRadius: '999px',
              border: `2px solid ${textColors.continue_button_border}`,
              color: textColors.continue_button,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            {continueButtonLabel}
          </Button>
        </Stack>

        {partners.length > 0 ? (
          <Box>
            <Typography
              sx={{
                fontFamily: PETRONA,
                fontStyle: 'italic',
                fontWeight: 700,
                color: textColors.events_section_title,
                fontSize: '1rem',
                mb: 1,
              }}
            >
              {eventsSectionTitle}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}>
              {partners.map((partner) => (
                <Box key={partner.provider_id} sx={partnerCardWrapSx()}>
                  <PreviewPartnerCard
                    partner={partner}
                    searchLabel={searchButtonLabel}
                    calendarIcon={icons.calendar}
                    locationIcon={icons.location}
                    cardTitleColor={textColors.card_title}
                    cardMetaColor={textColors.card_meta}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px dashed #CBD5E1',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 180,
              color: '#64748B',
              fontSize: 14,
            }}
          >
            Sélectionnez au moins un partenaire pour prévisualiser les cartes.
          </Box>
        )}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid #E2E8F0',
          px: 2.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          bgcolor: '#F8FAFC',
        }}
      >
        <PreviewIcon
          src={icons.lock}
          fallback={<LockOutlinedIcon sx={{ fontSize: 14, color: textColors.footer_text }} />}
          size={14}
        />
        <Typography sx={{ fontSize: 12, color: textColors.footer_text }}>{footerText}</Typography>
      </Box>
    </Box>
  );
};

export default HomeSplashScreenPreview;
